// HFP v3 -> HfpV2Report adapter (TD-210: content update only, NO redesign).
//
// The Family & School report keeps the existing HfpV2Report layout, structure
// and visual design; this adapter shapes the v3 persisted scoring payload
// (domains.hfpV3.scoring) into the report model that component consumes, so a
// v3 record renders through the exact same surface a v2 record does.
//
// Content rules owned here (build instruction section 5.4):
//  - ratings come from the shared engine's persisted payload (cross-surface
//    identity with the clinical view)
//  - service-user layer names from the crosswalk throughout
//  - sensory areas surface the RESOLVED bands; Strength folds into the family
//    green word; no means/scores are carried (the family view has no numbers)
//  - environmental areas read the barrier/facilitator words
//  - the calibration line rides as the report footer note
import crosswalk from '../schemas/hfpV3/crosswalk.json';

const SEVERITY = { strength: 0, none: 0, support: 1, priority: 2 };
const GREY = ['insufficient', 'notApplicable'];

// Strength folds into the green family word. Exported so display layers key
// off these exact strings instead of retyping them (hfpDisplay.js).
export const FAMILY_WORDS = {
  strength: 'Strength / no difficulty',
  none: 'Strength / no difficulty',
  support: 'Support need',
  priority: 'Priority area',
  insufficient: 'Insufficient data',
  notApplicable: 'Not applicable',
};
export const FAMILY_ENV_WORDS = {
  strength: 'Facilitators in place',
  none: 'No barriers',
  support: 'Barriers present',
  priority: 'Severe barriers',
  insufficient: 'Insufficient data',
  notApplicable: 'Not applicable',
};

export const CALIBRATION_NOTE =
  'Ratings use thresholds that are under clinical calibration. This profile describes functioning; it is not a diagnosis.';

const subRow = (domain, subdomain) =>
  crosswalk.find((r) => r.level === 'subdomain' && r.domain === domain && r.subdomain === subdomain);

const suSubName = (domain, subdomain) => (subRow(domain, subdomain) || {}).suName || subdomain;

function functionalRows(scoring, domain) {
  const home = (scoring.contexts.home.functional[domain] || {}).subdomains || {};
  const school = (scoring.contexts.school.functional[domain] || {}).subdomains || {};
  return Object.keys(home).map((sub) => {
    const h = home[sub] || {};
    const s = school[sub] || {};
    return {
      subdomain: sub,
      subdomain_plain: suSubName(domain, sub),
      home_label: FAMILY_WORDS[h.rating] || 'Insufficient data',
      school_label: FAMILY_WORDS[s.rating] || 'Insufficient data',
      // the panels display these verbatim (keeps the Not applicable /
      // Insufficient data distinction the bucket text would collapse)
      home_label_text: FAMILY_WORDS[h.rating] || 'Insufficient data',
      school_label_text: FAMILY_WORDS[s.rating] || 'Insufficient data',
      home_mean: null, // the family view carries no numbers
      school_mean: null,
      is_sensory: false,
      home_only: s.rating === 'notApplicable',
    };
  });
}

function sensoryRows(scoring) {
  const home = scoring.contexts.home.senses;
  const school = scoring.contexts.school.senses;
  return Object.keys(home).map((sense) => ({
    subdomain: sense,
    // family surfaces show the service-user sense names (Sight, Hearing, ...)
    subdomain_plain: suSubName('Sensory', sense),
    home_label: FAMILY_WORDS[home[sense].band] || 'Insufficient data',
    school_label: FAMILY_WORDS[(school[sense] || {}).band] || 'Insufficient data',
    home_label_text: FAMILY_WORDS[home[sense].band] || 'Insufficient data',
    school_label_text: FAMILY_WORDS[(school[sense] || {}).band] || 'Insufficient data',
    home_mean: null,
    school_mean: null,
    is_sensory: false, // v3 senses read the resolved band words, not v2 tag labels
  }));
}

function environmentalSubdomains(scoring, childName, envNarratives = null) {
  const domain = 'Environmental Factors';
  const home = (scoring.contexts.home.functional[domain] || {}).subdomains || {};
  const school = (scoring.contexts.school.functional[domain] || {}).subdomains || {};
  return Object.keys(home).map((sub) => {
    const homeWord = FAMILY_ENV_WORDS[(home[sub] || {}).rating] || 'Insufficient data';
    const schoolWord = FAMILY_ENV_WORDS[(school[sub] || {}).rating] || 'Insufficient data';
    return {
      name: suSubName(domain, sub),
      home: { items: [] },
      school: { items: [] },
      home_label: homeWord,
      school_label: schoolWord,
      narrative:
        (envNarratives || {})[suSubName(domain, sub)] ||
        `How well ${childName}'s environment meets their needs here: at home — ${homeWord.toLowerCase()}; at school — ${schoolWord.toLowerCase()}.`,
    };
  });
}

// Merge the server-generated narrative payload (rmsGenerateHfpV3Report — the
// v2 writer/editor chain rebound to v3) into the deterministic rows. Rows are
// matched by internal subdomain; the polished sets pass through verbatim under
// the exact keys HfpV2Report reads (_polished[domain].polished[suName]).
function mergeNarratives(model, narratives) {
  if (!narratives) return model;
  const graft = (targetAreas, sourceAreas) => {
    Object.entries(targetAreas || {}).forEach(([domain, data]) => {
      const srcRows = ((sourceAreas || {})[domain] || {}).rows || [];
      (data.rows || []).forEach((row) => {
        const src = srcRows.find((r) => r.subdomain === row.subdomain);
        if (!src) return;
        if (src.llm_paragraph !== undefined) row.llm_paragraph = src.llm_paragraph;
        if (src.llm_error !== undefined) row.llm_error = src.llm_error;
        if (src.llm_outputs) row.llm_outputs = src.llm_outputs;
      });
    });
  };
  graft(model.areas_of_development, narratives.areas_of_development);
  graft(model.gpi_areas, narratives.gpi_areas);
  if (narratives.suggested_adjustments) {
    // the matcher's groups already carry the family sub-domain name, the
    // bucket strings the component reads ('Priority Area'/'Support Need')
    // and the ranked, capped, de-duplicated adjustments
    model.suggested_adjustments = {
      home: { groups: (narratives.suggested_adjustments.home || {}).groups || [] },
      school: { groups: (narratives.suggested_adjustments.school || {}).groups || [] },
    };
  }
  if (narratives._polished) model._polished = narratives._polished;
  if (narratives._gpi_polished) model._gpi_polished = narratives._gpi_polished;
  if (narratives._llm_meta) model._llm_meta = narratives._llm_meta;
  return model;
}

/**
 * Shape a v3 scoring payload (domains.hfpV3.scoring) into the report model
 * HfpV2Report consumes, so the existing Family & School report design renders
 * v3 content unchanged. Pass the rmsGenerateHfpV3Report payload as
 * `narratives` to fill the per-area AI paragraphs (generated by the same
 * writer/editor chain as v2); without it the component shows its usual
 * placeholder.
 */
export default function buildHfpV3ReportModel(scoring, childName = 'the child', narratives = null) {
  const areas = {};
  ['Executive Function', 'Social & Emotional', 'Motor & Physical', 'Speech, Language & Communication'].forEach(
    (domain) => {
      areas[domain] = { rows: functionalRows(scoring, domain) };
    }
  );
  areas.Sensory = { rows: sensoryRows(scoring) };

  const painOverride = ['home', 'school'].some(
    (ctx) => (((scoring.contexts || {})[ctx] || {}).senses || {}).Interoception?.painOverride
  );
  const domainNames = {};
  crosswalk
    .filter((r) => r.level === 'domain')
    .forEach((r) => {
      if (r.suName) domainNames[r.domain] = r.suName;
    });
  domainNames.gpi = domainNames['Global Participation Indicators'] || 'Everyday life';
  domainNames.environmental = domainNames['Environmental Factors'] || 'Things around them';

  const model = {
    _v3: true,
    _calibration_note: CALIBRATION_NOTE,
    child_name: childName,
    about_this_profile: {
      heading: 'About this profile',
      paragraphs: [
        `This report describes how ${childName} is getting on across everyday areas of development — what comes easily, and where a little more support helps — seen both at home and at school. There are no scores here: each area shows a simple status.`,
      ],
    },
    areas_of_development: areas,
    // family headings for the nav and page titles (INSTRUCTION 2026-07-22)
    _domain_names: domainNames,
    // drives the FIXED pain safety note row — never model-generated
    _pain_safety: painOverride || null,
    gpi_areas: {
      'Global Participation Indicators': {
        rows: functionalRows(scoring, 'Global Participation Indicators'),
      },
    },
    environmental_summary: {
      subdomains: environmentalSubdomains(
        scoring, childName, narratives && narratives.environmental_narratives
      ),
    },
    // suggested_adjustments deliberately absent: pending the v3 re-spec, the
    // report renders its "being prepared" note instead of stale v2 matching.
  };
  return mergeNarratives(model, narratives);
}
